interface CategoriesTabProps {
  filteredCategories: Array<{ name: string; count: number }>;
  selectedCategories: string[];
  selectCategory: (category: string) => void;
}

// Category-specific gradient mapping with DUAL VERTICAL CORNER-TO-CORNER CASCADE
// LEFT COLUMN (to-br): to → next from
// RIGHT COLUMN (to-bl): via → next from
const categoryGradients: Record<string, string> = {
  // ===== LEFT COLUMN CASCADE (to-br direction) =====
  "Arts": "from-red-600 via-red-600 to-orange-500",
  "Sports": "from-orange-500 via-cyan-600 to-green-500",
  "Sustainability": "from-cyan-600 via-green-500 to-green-700",
  "Accessibility": "from-green-700 via-yellow-300 to-yellow-300",
  
  // ===== RIGHT COLUMN CASCADE (to-bl direction) =====
  "History": "from-red-500 via-red-500 to-fuchsia-600",
  "Nightlife": "from-red-500 via-fuchsia-600 to-blue-400",
  "Technology": "from-fuchsia-600 via-blue-400 to-blue-700",
  "Mobility": "from-blue-400 via-blue-700 to-lime-400",
  "Family": "from-blue-700 via-lime-400 to-yellow-500",
  
  // Meta/Neutral
  "OSM Projects": "from-slate-500 to-gray-600",
  "Last Added": "from-gray-600 to-slate-500",
};

// Category gradient directions (LEFT column: to-br, RIGHT column: to-bl)
const categoryDirections: Record<string, 'to-br' | 'to-bl'> = {
  // Left column
  "Arts & Culture": "to-br",
  "Food & Drinks": "to-br",
  "Sports": "to-br",
  "Sustainability": "to-br",
  "Nature & Outdoor": "to-br",
  "Accessibility": "to-br",
  
  // Right column
  "History": "to-bl",
  "Nightlife": "to-bl",
  "Technology": "to-bl",
  "Mobility": "to-bl",
  "Family": "to-bl",
  "Health & Support": "to-bl",
  
  // Meta/Neutral (can be either, using to-br)
  "OSM Projects": "to-br",
  "Last Added": "to-br",
};

// Define which categories should be paired together with cascade colors
const categoryPairs = [
  { 
    display: "Arts & Culture", 
    categories: ["Arts", "Culture"], 
    gradient: "from-red-600 via-red-600 to-orange-500"
  },
  { 
    display: "Food & Drinks", 
    categories: ["Food", "Drinks"], 
    gradient: "from-red-600 via-orange-500 to-cyan-600"
  },
  { 
    display: "Nature & Outdoor", 
    categories: ["Nature", "Outdoor"], 
    gradient: "from-green-500 via-green-700 to-yellow-300"
  },
  { 
    display: "Health & Support", 
    categories: ["Health", "Support"], 
    gradient: "from-lime-400 via-yellow-500 to-yellow-500"
  }
];

// Grouped category order
const categoryOrder = [
  "Arts", "Culture",
  "History",
  "Food", "Drinks",
  "Nightlife",
  "Sports",
  "Technology",
  "Sustainability",
  "Mobility",
  "Nature", "Outdoor",
  "Family",
  "Accessibility",
  "Health", "Support",
  "OSM Projects",
  "Last Added",
];

export function CategoriesTab({
  filteredCategories,
  selectedCategories,
  selectCategory,
}: CategoriesTabProps) {
  // Create a map for quick lookup of category counts
  const categoryMap = new Map(
    filteredCategories.map(cat => [cat.name, cat.count])
  );

  // Build button list maintaining order, combining pairs where appropriate
  const renderedCategories = new Set<string>();
  const buttons: Array<{
    type: 'single' | 'pair';
    key: string;
    display: string;
    onClick: () => void;
    isSelected: boolean;
    gradient: string;
    direction: string;
    isLastAdded: boolean;
    isPaired: boolean;
    ariaLabel: string;
  }> = [];

  for (const categoryName of categoryOrder) {
    if (renderedCategories.has(categoryName)) continue;
    if (!categoryMap.has(categoryName)) continue;

    // Check if this category is part of a pair
    const pair = categoryPairs.find(p => p.categories.includes(categoryName));

    if (pair) {
      // This is a paired category
      const availableCategories = pair.categories.filter(cat => categoryMap.has(cat));

      if (availableCategories.length > 0) {
        const isSelected = availableCategories.some(cat => selectedCategories.includes(cat));

        buttons.push({
          type: 'pair',
          key: pair.display,
          display: pair.display,
          onClick: () => {
            // If any category in pair is selected, deselect all; otherwise select all
            if (isSelected) {
              availableCategories.forEach(cat => {
                if (selectedCategories.includes(cat)) {
                  selectCategory(cat); // Deselect
                }
              });
            } else {
              availableCategories.forEach(cat => {
                if (!selectedCategories.includes(cat)) {
                  selectCategory(cat); // Select
                }
              });
            }
          },
          isSelected,
          gradient: pair.gradient,
          direction: categoryDirections[pair.display] || "to-br",
          isLastAdded: false,
          isPaired: true,
          ariaLabel: `Select ${pair.display}`
        });

        // Mark all categories in this pair as rendered
        pair.categories.forEach(cat => renderedCategories.add(cat));
      }
    } else {
      // Regular single category
      buttons.push({
        type: 'single',
        key: categoryName,
        display: categoryName,
        onClick: () => selectCategory(categoryName),
        isSelected: selectedCategories.includes(categoryName),
        gradient: categoryGradients[categoryName] || "from-blue-600 to-blue-700",
        direction: categoryDirections[categoryName] || "to-br",
        isLastAdded: categoryName === "Last Added" || categoryName === "OSM Projects",
        isPaired: false,
        ariaLabel: `Select ${categoryName}`
      });

      renderedCategories.add(categoryName);
    }
  }

  return (
    <div className="min-h-full flex flex-col justify-end">
      <div className="max-w-lg mx-auto w-full px-1">
        <div className="grid grid-cols-2 gap-3 pt-2 pb-2">
          {buttons
            .filter(button => !button.isLastAdded)
            .map((button) => {
            return (
              <button
                key={button.key}
                onClick={button.onClick}
                className={`
                  category-button
                  relative rounded-[15px]
                  group
                  ${button.isSelected 
                    ? "bg-transparent border border-[var(--accent-blue)]" 
                    : "bg-white/8 border border-white/10"
                  }
                  hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
                  px-2 py-4
                  w-full
                  overflow-hidden
                `}
                aria-label={button.ariaLabel}
                aria-pressed={button.isSelected}
              >
                {/* Gradient background for selected state */}
                {button.isSelected && (
                  <span 
                    className={`
                      absolute inset-[-1px] rounded-[inherit] 
                      ${button.direction === 'to-br' ? 'bg-gradient-to-br' : 'bg-gradient-to-bl'} ${button.gradient} opacity-50
                      z-0
                    `}
                  />
                )}
                
                <p 
                  className={`
                    text-white relative z-10
                    text-base sm:text-lg md:text-[22px]
                    [text-shadow:_0_1px_3px_rgb(0_0_0_/_40%)]
                    text-center
                  `}
                >
                  {button.display}
                </p>
              </button>
            );
          })}
        </div>
        
        {/* Special buttons row */}
        <div className="flex justify-center gap-3 mt-1 pb-1">
          {buttons
            .filter(button => button.isLastAdded)
            .map((button) => {
            return (
              <button
                key={button.key}
                onClick={button.onClick}
                className={`
                  category-button
                  relative rounded-[15px]
                  group
                  ${button.isSelected 
                    ? "bg-transparent border border-[var(--accent-blue)]" 
                    : "bg-white/8 border border-white/10"
                  }
                  hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
                  px-2 py-2 md:px-3 md:py-3
                  w-32 sm:w-36
                  overflow-hidden
                `}
                aria-label={button.ariaLabel}
                aria-pressed={button.isSelected}
              >
                {/* Gradient background for selected state */}
                {button.isSelected && (
                  <span 
                    className={`
                      absolute inset-[-1px] rounded-[inherit] 
                      ${button.direction === 'to-br' ? 'bg-gradient-to-br' : 'bg-gradient-to-bl'} ${button.gradient} opacity-70
                      z-0
                    `}
                  />
                )}
                
                <p 
                  className={`
                    text-white relative z-10
                    text-sm sm:text-base md:text-lg
                    [text-shadow:_0_1px_3px_rgb(0_0_0_/_40%)]
                    text-center
                  `}
                >
                  {button.display}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}